import { ReactElement, useEffect, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';

const COLORS = [
  '#2563eb',
  '#16a34a',
  '#f97316',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#f59e0b',
  '#ef4444',
  '#10b981',
  '#6366f1',
];

interface SeriesEntry {
  letter?: string | null;
  x_name?: string;
  y_name?: string;
  x_series?: number[];
  y_series?: number[];
  y_fit_series?: number[];
  x?: number[];
  y?: number[];
  slope?: number | null;
  weight?: number[];
}

interface CrrTempFullPlotProps {
  data?: Record<string, SeriesEntry> | null;
  title?: string;
  height?: number;
  isPercentage?: boolean;
}

const extractSeries = (entry?: SeriesEntry | null): { x: number[]; y: number[]; fit: number[] } => {
  if (!entry) {
    return { x: [], y: [], fit: [] };
  }
  const x = Array.isArray(entry.x_series)
    ? entry.x_series
    : Array.isArray(entry.x)
      ? entry.x
      : [];
  const y = Array.isArray(entry.y_series)
    ? entry.y_series
    : Array.isArray(entry.y)
      ? entry.y
      : [];
  const fit = Array.isArray(entry.y_fit_series)
    ? entry.y_fit_series
    : [];

  return { x, y, fit };
};

export default function CrrTempFullPlot(
  { data, title, height = 360, isPercentage = false }: CrrTempFullPlotProps
): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const plotElement = plotRef.current;
    if (!plotElement) {
      return undefined;
    }

    const entries = data ? Object.entries(data) : [];
    if (!entries.length) {
      Plotly.purge(plotElement);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const traces: any[] = [];
    const annotations: any[] = [];
    const allX: number[] = [];
    const allY: number[] = [];

    entries.forEach(([key, entry], index) => {
      const { x, y, fit } = extractSeries(entry);
      const count = Math.min(x.length, y.length);
      if (!count) {
        return;
      }

      const sorted = Array.from({ length: count })
        .map((_, idx) => ({ temp: x[idx], value: y[idx] }))
        .filter(({ temp, value }) => temp !== null && temp !== undefined && value !== null && value !== undefined)
        .sort((a, b) => a.temp - b.temp);

      if (!sorted.length) {
        return;
      }

      const color = COLORS[index % COLORS.length];
      const label = entry?.letter || key;

      traces.push({
        x: sorted.map((point) => point.temp),
        y: sorted.map((point) => isPercentage ? point.value * 100 : point.value),
        mode: 'markers',
        type: 'scatter',
        name: `Cfg ${label}`,
        marker: {
          color,
          size: 9,
          opacity: 0.85,
          line: { width: 1, color: '#fff' },
        },
        hovertemplate:
          `<b>Cfg ${label}</b><br>` +
          'Temperature: %{x:.1f} °C<br>' +
          (isPercentage ? 'Value: %{y:.4f}<br>' : 'Value: %{y:.6f}<br>') +
          '<extra></extra>',
      });

      if (fit.length) {
        const fitPairs = Array.from({ length: Math.min(x.length, fit.length) })
          .map((_, idx) => ({ temp: x[idx], value: fit[idx] }))
          .filter(({ temp, value }) => temp !== null && temp !== undefined && value !== null && value !== undefined)
          .sort((a, b) => a.temp - b.temp);

        if (fitPairs.length) {
          traces.push({
            x: fitPairs.map((pair) => pair.temp),
            y: fitPairs.map((pair) => isPercentage ? pair.value * 100 : pair.value),
            mode: 'lines',
            type: 'scatter',
            name: `Cfg ${label} Fit`,
            line: { color, width: 2, dash: 'dash' },
            hovertemplate:
              `<b>Cfg ${label} Fit</b><br>` +
              'Temperature: %{x:.1f} °C<br>' +
              (isPercentage ? 'Value: %{y:.4f}<br>' : 'Value: %{y:.6f}<br>') +
              '<extra></extra>',
            showlegend: false,
          });
        }
      }

      sorted.forEach((point) => {
        allX.push(point.temp);
        allY.push(isPercentage ? point.value * 100 : point.value);
      });

      const slope = typeof entry?.slope === 'number' ? entry?.slope : null;
      if (slope !== null) {
        annotations.push({
          text: `Cfg ${label} Slope = ${slope.toExponential(3)}`,
          xref: 'paper',
          yref: 'paper',
          x: 0.98,
          y: 1.05 - annotations.length * 0.06,
          xanchor: 'right',
          showarrow: false,
          font: { size: 12, color: '#374151' },
          align: 'right',
        });
      }
    });

    if (!traces.length) {
      Plotly.purge(plotElement);
      setIsLoading(false);
      return undefined;
    }

    const minTemp = allX.length ? Math.min(...allX) : undefined;
    const maxTemp = allX.length ? Math.max(...allX) : undefined;
    const padding = minTemp !== undefined && maxTemp !== undefined ? Math.max((maxTemp - minTemp) * 0.05, 1) : 1;
    const minValue = allY.length ? Math.min(...allY) : undefined;
    const maxValue = allY.length ? Math.max(...allY) : undefined;
    const valuePadding = minValue !== undefined && maxValue !== undefined
      ? Math.max((maxValue - minValue) * 0.05, isPercentage ? 0.5 : 0.00001)
      : (isPercentage ? 0.5 : 0.00001);

    const layout: Partial<Plotly.Layout> = {
      title: {
        text: title || (isPercentage ? 'Crr % Change' : 'Crr'),
        font: { family: 'Poppins', size: 16 },
      },
      xaxis: {
        title: { text: 'Temperature °C', font: { family: 'Poppins' } },
        type: 'linear',
        range:
          minTemp !== undefined && maxTemp !== undefined
            ? [minTemp - padding, maxTemp + padding]
            : undefined,
        autorange: minTemp === undefined,
      },
      yaxis: {
        title: { text: isPercentage ? 'Crr % Change' : 'Crr', font: { family: 'Poppins' } },
        range:
          minValue !== undefined && maxValue !== undefined
            ? [minValue - valuePadding, maxValue + valuePadding]
            : undefined,
        autorange: minValue === undefined,
      },
      legend: {
        x: 0,
        y: 1.1,
        xanchor: 'left',
        bgcolor: 'rgba(255,255,255,0.85)',
        orientation: typeof window !== 'undefined' && window.innerWidth < 1024 ? 'h' : 'v',
      },
      margin: { l: 70, r: 30, t: 60, b: 50 },
      hovermode: 'closest',
      plot_bgcolor: '#f8f9fa',
      paper_bgcolor: 'white',
      font: { family: 'Poppins' },
      annotations: annotations.length ? annotations : undefined,
    };

    const config: Partial<Plotly.Config> = {
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      responsive: true,
    };

    Plotly.newPlot(plotElement, traces, layout, config).then(() => {
      setIsLoading(false);
    });

    return () => {
      Plotly.purge(plotElement);
    };
  }, [data, title, isPercentage]);

  if (!data) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-gray-500">
        No repeat parameter data available
      </div>
    );
  }

  return (
    <div className="cr-relative cr-w-full" style={{ height }}>
      {isLoading && (
        <div className="cr-absolute cr-inset-0 cr-z-10 cr-flex cr-items-center cr-justify-center cr-bg-white cr-bg-opacity-75">
          <div className="cr-h-8 cr-w-8 cr-animate-spin cr-rounded-full cr-border-4 cr-border-gray-300 cr-border-t-blue-600" />
        </div>
      )}
      <div ref={plotRef} className="cr-h-full cr-w-full" />
    </div>
  );
}

